import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  makeStyles,
} from "@material-ui/core";
import { useState } from "react";
import { useSelector } from "react-redux";
import { authSelector } from "../../../redux/selectors";
import { complaintsService, peService, sarsService } from "../../../services";
import { ItemHeaderProps } from "../ItemHeader";
import { Skeleton } from "@material-ui/lab";
import { DialogCreateActionDecider } from "./DialogCreateActionDecider";
import { ActionButtonRenderer } from "./renderers/ActionButtonRenderer";
import { ExperimentalFeature } from "@ofqual/portal-components/components/ExperimentalFeature";

export type ActionSelectorDialogProps = {
  itemHeaderProps: ItemHeaderProps;
  onClose: () => void;
  open: boolean;
  disabled: boolean;
};

const useStyles = makeStyles((theme) => ({
  dialog: {
    minWidth: "500px",
    padding: "20px",
  },
}));

export type DialogDeciderActionTypes = {
  action: "Complaint" | "PE" | "SAR" | null;
};

export const ActionSelectorDialog: React.FC<ActionSelectorDialogProps> = ({
  itemHeaderProps: { state },
  onClose,
  open,
  disabled,
}) => {
  const item = state?.item;
  const classes = useStyles();
  const [dialogAction, setDialogAction] = useState<DialogDeciderActionTypes>({
    action: null,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [id, setId] = useState<string | null>(null);
  const [isCreateDialog, setIsCreateDialog] = useState<boolean | null>(null);
  const { token, experimentalFeatures } = useSelector(authSelector);

  const handleCreatePe = async () => {
    if (item !== undefined) {
      const getPe = async () => {
        setLoading(true);
        const res = await peService.getPeFromFoiId(item.id, { token });
        if (res.status === 204) {
          setId(res.data.id);
          setIsCreateDialog(false);
        } else if (res.status === 404) {
          setId(null);
          setIsCreateDialog(true);
        }
        setDialogAction({ action: "PE" });
        setLoading(false);
      };
      getPe();
    }
  };

  const handleCreateComplaint = async () => {
    if (item !== undefined) {
      const getPe = async () => {
        setLoading(true);
        const res = await complaintsService.getComplaintFromFoiId(item.id, {
          token,
        });
        if (res.status === 204) {
          setId(res.data.id);
          setIsCreateDialog(false);
        } else if (res.status === 404) {
          setId(null);
          setIsCreateDialog(true);
        }
        setDialogAction({ action: "Complaint" });
        setLoading(false);
      };
      getPe();
    }
  };


    const handleCreateSar = async () => {
      if (item !== undefined) {
        const getSar = async () => {
          setLoading(true);
          const res = await sarsService.getSarFromFoiId(item.id, {
            token,
          });
          if (res.status === 204) {
            setId(res.data.id);
            setIsCreateDialog(false);
          } else if (res.status === 404) {
            setId(null);
            setIsCreateDialog(true);
          }
          setDialogAction({ action: "SAR" });
          setLoading(false);
        };
        getSar();
      }
    };


  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          onClose();
          setDialogAction({ action: null });
        }}
        fullWidth
        maxWidth="md"
      >
        {!loading ? (
          <>
            {!disabled ? (
              <>
                {dialogAction.action === null ? (
                  <DialogContent>
                    <List>
                      <ListItem
                        data-test="transfer-pe-button"
                        button
                        onClick={() => handleCreatePe()}
                      >
                        <ListItemText primary="PE" />
                      </ListItem>

                      <ExperimentalFeature
                        experimentalModuleName="portal-foi"
                        experimentalFeatures={experimentalFeatures}
                        experimentalFeatureVisibleName="foi-transfer-to-cwm"
                      > 
                        <ListItem
                          data-test="transfer-complaints-button"
                          onClick={() => handleCreateComplaint()}
                          button
                        >
                          <ListItemText primary="Complaints" />
                        </ListItem>
                      </ExperimentalFeature>

                      <ExperimentalFeature
                        experimentalModuleName="portal-foi"
                        experimentalFeatures={experimentalFeatures}
                        experimentalFeatureVisibleName="foi-transfer-to-sar"
                      > 
                        <ListItem
                          data-test="transfer-sar-button"
                          onClick={() => handleCreateSar()}
                          button
                        >
                          <ListItemText primary="SAR" />
                        </ListItem>
                      </ExperimentalFeature>

                    </List>
                  </DialogContent>
                ) : (
                  <>
                    {isCreateDialog !== null && isCreateDialog === true ? (
                      <DialogCreateActionDecider
                        item={item}
                        actionDialog={{
                          action: dialogAction.action,
                        }}
                      />
                    ) : (
                      <DialogContent data-test="validation-message">
                        A {dialogAction.action} already exists for this FOI
                      </DialogContent>
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <DialogContent
                  data-test="item-disabled-dialog"
                  className={classes.dialog}
                >
                  Item is either closed or cancelled and cannot be transferred.
                </DialogContent>
              </>
            )}
          </>
        ) : (
          <DialogContent className={classes.dialog}>
            <Skeleton variant="text" height={60} />
            <Skeleton variant="text" height={60} />
          </DialogContent>
        )}
        {!loading && (
          <DialogActions>
            <Button
              color="primary"
              onClick={() => {
                onClose();
                setDialogAction({ action: null });
              }}
              data-test="action-dialog-close-button"
            >
              Close
            </Button>
            {isCreateDialog !== null && isCreateDialog === false && !!id && (
              <ActionButtonRenderer id={id} dialogAction={dialogAction}>
                Go to {dialogAction.action}
              </ActionButtonRenderer>
            )}
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};
